import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  CreditCard,
  Receipt,
  TrendingUp,
  Clock,
  Sparkles,
  BarChart3,
  Key,
  Package,
  DollarSign,
  Loader2,
  ArrowLeft,
  MessageCircle,
  Crown,
  Mail,
  Wallet,
  Video,
  Megaphone,
  Activity,
  MessageSquare,
  FileText,
  Ticket,
  Terminal,
  Gift
} from "lucide-react";

import ApiSettings from "@/components/admin/ApiSettings";
import PricingSettings from "@/components/admin/PricingSettings";
import UsageReport from "@/components/admin/UsageReport";
import UserManagement from "@/components/admin/UserManagement";
import ChatLogs from "@/components/admin/ChatLogs";
import FAQManagement from "@/components/admin/FAQManagement";
import LicenseManagement from "@/components/admin/LicenseManagement";
import ProfessionalManagement from "@/components/admin/ProfessionalManagement";
import UserGenerationLogs from "@/components/admin/UserGenerationLogs";
import EmailMarketing from "@/components/admin/EmailMarketing";
import EmailTemplates from "@/components/admin/EmailTemplates";
import PaymentSettings from "@/components/admin/PaymentSettings";
import OrderManagement from "@/components/admin/OrderManagement";
import MagicButtonManagement from "@/components/admin/MagicButtonManagement";
import CloneVDOManagement from "@/components/admin/CloneVDOManagement";
import AnnouncementManagement from "@/components/admin/AnnouncementManagement";
import ArticleManagement from "@/components/admin/ArticleManagement";
import KieLogs from "@/components/admin/KieLogs";
import SupportManagement from "@/components/admin/SupportManagement";
import CouponManagement from "@/components/admin/CouponManagement";
import PromptAiManagement from "@/components/admin/PromptAiManagement";
import PromoEmailManagement from "@/components/admin/PromoEmailManagement";
import SimpleEmailSend from "@/components/admin/SimpleEmailSend";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";

import { supabase } from "@/integrations/supabase/client";

import { getVideoCloneStatistics } from "@/lib/video-clone-service";
import { Badge } from "@/components/ui/badge";

const AdminPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const userIdParam = searchParams.get("userId");
  const [activeTab, setActiveTab] = useState(tabParam || "orders");

  useEffect(() => {
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const handleTabChange = (val: string) => {
    setActiveTab(val);
    const params: Record<string, string> = { tab: val };
    if (val === "history" && userIdParam) {
      params.userId = userIdParam;
    }
    setSearchParams(params);
  };

  const { user, profile, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCredits: 0,
    totalGenerations: 0,
    pendingOrders: 0,
    pendingVideoClones: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !adminLoading) {
      if (!user) {
        navigate("/login");
      } else if (!isAdmin) {
        navigate("/dashboard");
      }
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!isAdmin) return;

      try {
        // Get total users count
        const { count: usersCount } = await supabase
          .from("profiles")
          .select("*", { count: "exact", head: true });

        // Get total credits across all users
        const { data: creditsData } = await supabase
          .from("profiles")
          .select("credits");

        const totalCredits = creditsData?.reduce((sum, p) => sum + (p.credits || 0), 0) || 0;

        // Get total generations count
        const { count: generationsCount } = await supabase
          .from("generation_logs")
          .select("*", { count: "exact", head: true });

        // Get pending video clone statistics
        const videoStats = await getVideoCloneStatistics();

        setStats({
          totalUsers: usersCount || 0,
          totalCredits: totalCredits,
          totalGenerations: generationsCount || 0,
          pendingOrders: 0,
          pendingVideoClones: videoStats.pending,
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setStatsLoading(false);
      }
    };

    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin]);

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  const statsDisplay = [
    { label: "Total Users", value: statsLoading ? "..." : stats.totalUsers.toLocaleString(), icon: Users, trend: "" },
    { label: "Total Credits", value: statsLoading ? "..." : stats.totalCredits.toLocaleString(), icon: CreditCard, trend: "" },
    { label: "Total Generations", value: statsLoading ? "..." : stats.totalGenerations.toLocaleString(), icon: TrendingUp, trend: "" },
    { label: "Pending Orders", value: statsLoading ? "..." : stats.pendingOrders.toLocaleString(), icon: Clock, trend: "" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <Sparkles className="h-6 w-6 text-primary" />
              <span className="text-xl font-heading font-bold">Admin Panel</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{profile?.email || user.email}</span>
              <Link to="/dashboard">
                <Button variant="outline" size="sm">Dashboard</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statsDisplay.map((stat, index) => (
            <Card key={index} variant="glass">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    {stat.trend && (
                      <p className="text-xs text-primary">{stat.trend} this month</p>
                    )}
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <stat.icon className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6 flex-wrap h-auto gap-2">
            <TabsTrigger value="orders" className="gap-2">
              <Receipt className="h-4 w-4" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="licenses" className="gap-2">
              <Package className="h-4 w-4" />
              Licenses
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <Clock className="h-4 w-4" />
              History
            </TabsTrigger>
            <TabsTrigger value="chatlogs" className="gap-2">
              <MessageCircle className="h-4 w-4" />
              Chat Logs
            </TabsTrigger>
            <TabsTrigger value="faq" className="gap-2">
              <Sparkles className="h-4 w-4" />
              FAQ
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Usage Reports
            </TabsTrigger>
            <TabsTrigger value="api" className="gap-2">
              <Key className="h-4 w-4" />
              API Settings
            </TabsTrigger>
            <TabsTrigger value="pricing" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="payment" className="gap-2">
              <Wallet className="h-4 w-4" />
              Payment
            </TabsTrigger>
            <TabsTrigger value="professional" className="gap-2">
              <Crown className="h-4 w-4" />
              Professional Mode
            </TabsTrigger>
            <TabsTrigger value="magic" className="gap-2">
              <Sparkles className="h-4 w-4" />
              Magic Button
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-2">
              <Mail className="h-4 w-4" />
              Email Marketing
            </TabsTrigger>
            <TabsTrigger value="clone-vdo" className="gap-2 relative">
              <Video className="h-4 w-4" />
              Clone VDO
              {stats.pendingVideoClones > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-[10px]">
                  {stats.pendingVideoClones}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="announcements" className="gap-2">
              <Megaphone className="h-4 w-4" />
              Announcements
            </TabsTrigger>
            <TabsTrigger value="kie-logs" className="gap-2">
              <Activity className="h-4 w-4" />
              KIE Logs
            </TabsTrigger>
            <TabsTrigger value="support" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Support
            </TabsTrigger>
            <TabsTrigger value="articles" className="gap-2">
              <FileText className="h-4 w-4" />
              Articles
            </TabsTrigger>
            <TabsTrigger value="coupons" className="gap-2">
              <Ticket className="h-4 w-4" />
              Coupons
            </TabsTrigger>
            <TabsTrigger value="prompt-ai" className="gap-2">
              <Terminal className="h-4 w-4" />
              Prompt AI
            </TabsTrigger>
            <TabsTrigger value="promo-emails" className="gap-2">
              <Gift className="h-4 w-4" />
              Promo Emails
            </TabsTrigger>
          </TabsList>


          {/* Orders Tab */}
          <TabsContent value="orders">
            <OrderManagement />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          {/* Licenses Tab */}
          <TabsContent value="licenses">
            <LicenseManagement />
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <UserGenerationLogs userId={userIdParam} />
          </TabsContent>

          {/* Chat Logs Tab */}
          <TabsContent value="chatlogs">
            <ChatLogs />
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <FAQManagement />
          </TabsContent>

          {/* Usage Reports Tab */}
          <TabsContent value="reports">
            <UsageReport />
          </TabsContent>

          {/* API Settings Tab */}
          <TabsContent value="api">
            <ApiSettings />
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <PricingSettings />
          </TabsContent>

          {/* Payment Settings Tab */}
          <TabsContent value="payment">
            <PaymentSettings />
          </TabsContent>

          {/* Professional Mode Tab */}
          <TabsContent value="professional">
            <ProfessionalManagement />
          </TabsContent>

          {/* Magic Button Tab */}
          <TabsContent value="magic">
            <MagicButtonManagement />
          </TabsContent>

          {/* Email Marketing Tab */}
          <TabsContent value="email">
            <Tabs defaultValue="quick-send">
              <TabsList className="mb-4">
                <TabsTrigger value="quick-send">Quick Send</TabsTrigger>
                <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
                <TabsTrigger value="templates">Templates</TabsTrigger>
              </TabsList>
              <TabsContent value="quick-send">
                <SimpleEmailSend />
              </TabsContent>
              <TabsContent value="campaigns">
                <EmailMarketing />
              </TabsContent>
              <TabsContent value="templates">
                <EmailTemplates />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Clone VDO Tab */}
          <TabsContent value="clone-vdo">
            <CloneVDOManagement />
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements">
            <AnnouncementManagement />
          </TabsContent>

          {/* KIE Logs Tab */}
          <TabsContent value="kie-logs">
            <KieLogs />
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support">
            <SupportManagement />
          </TabsContent>

          {/* Articles Tab */}
          <TabsContent value="articles">
            <ArticleManagement />
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <CouponManagement />
          </TabsContent>

          {/* Prompt AI Tab */}
          <TabsContent value="prompt-ai">
            <PromptAiManagement />
          </TabsContent>

          {/* Promo Emails Tab */}
          <TabsContent value="promo-emails">
            <PromoEmailManagement />
          </TabsContent>
        </Tabs>

      </main>
    </div>
  );
};

export default AdminPanel;
